import { Document, Paragraph, Packer, TextRun, Table, TableRow, TableCell, WidthType } from 'docx';
import type {
  ParticipantExportDataOutputType as ParticipantData,
  CoachingCallDetailOutputType as CoachingCall,
  IvrQuestionAnswerOutputType as QuestionAnswer,
} from '../../graphql';
import { abbreviation } from '../abbreviation';

const P = (text: string, bold = false, alignment: 'left' | 'center' | 'right' = 'left') =>
  new Paragraph({
    children: [new TextRun({ text, bold })],
    alignment,
  });

const createTitle = (text: string) =>
  new Paragraph({
    children: [new TextRun({ text, bold: true, size: 36 })],
    alignment: 'left',
    spacing: { after: 400 },
  });

const createCoachingTable = (call: CoachingCall, callIndex: number) => {
  const callDate = new Date(call.callDate).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const headerRow = new TableRow({
    children: [
      new TableCell({
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: `Call #${callIndex + 1} - ${callDate}`,
                bold: true,
                color: 'FFFFFF',
              }),
            ],
          }),
        ],
        shading: { fill: '0066CC' },
        width: { size: 70, type: WidthType.PERCENTAGE },
      }),
      new TableCell({
        children: [
          new Paragraph({
            children: [
              new TextRun({ text: `Coach: ${call.coachName}`, bold: true, color: 'FFFFFF' }),
            ],
          }),
        ],
        shading: { fill: '0066CC' },
        width: { size: 30, type: WidthType.PERCENTAGE },
      }),
    ],
  });

  const questionRows: TableRow[] = [];

  if (call.assessmentQuestions && call.assessmentQuestions.length > 0) {
    // Questions header
    questionRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: 'Question', bold: true, color: 'FFFFFF' })],
              }),
            ],
            shading: { fill: '4A90E2' },
            width: { size: 70, type: WidthType.PERCENTAGE },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: 'Answer', bold: true, color: 'FFFFFF' })],
              }),
            ],
            shading: { fill: '4A90E2' },
            width: { size: 30, type: WidthType.PERCENTAGE },
          }),
        ],
      }),
    );

    // Questions and answers
    for (const [qIndex, qa] of call.assessmentQuestions.entries()) {
      const answer = qa.answerValue || '(blank)';
      questionRows.push(
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: qa.questionText })],
                }),
              ],
              shading: { fill: qIndex % 2 === 0 ? 'FFFFFF' : 'F0F8FF' },
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: answer, bold: true })],
                }),
              ],
              shading: { fill: qIndex % 2 === 0 ? 'FFFFFF' : 'F0F8FF' },
            }),
          ],
        }),
      );
    }
  } else {
    questionRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph('No assessment questions recorded')],
            columnSpan: 2,
            shading: { fill: 'F8F9FA' },
          }),
        ],
      }),
    );
  }

  // Notes row if present
  if (call.notes) {
    questionRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: 'Notes', bold: true })],
              }),
            ],
            shading: { fill: 'E6F3FF' },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: call.notes })],
              }),
            ],
            shading: { fill: 'E6F3FF' },
          }),
        ],
      }),
    );
  }

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [headerRow, ...questionRows],
  });
};

export async function generateCoachingReportDocx(data: ParticipantData[]): Promise<Blob> {
  if (data.length === 0) {
    throw new Error('No participant data available');
  }

  const allDocumentChildren: (Paragraph | Table)[] = [];
  const studyHeader = data[0]?.studyName?.trim() || 'Study';

  // Add main title once
  allDocumentChildren.push(
    createTitle(studyHeader),
    P('Coaching Call Q&A Report', true, 'left'),
    P(`Report Generated: ${new Date().toLocaleDateString('en-US')}`, false, 'left'),
    P(''),
  );

  // Process each participant
  for (const pt of data) {
    const initials = abbreviation(pt.participantName);

    if (pt.coachingCalls.length === 0) {
      allDocumentChildren.push(
        P(`Patient Initials: ${initials}`, true),
        P('No coaching call data available', false),
        P(''),
      );
      continue;
    }

    allDocumentChildren.push(P(`Patient Initials: ${initials}`, true), P(''));

    for (const [index, call] of pt.coachingCalls.entries()) {
      allDocumentChildren.push(createCoachingTable(call, index), P(''));
    }
  }

  return Packer.toBlob(new Document({ sections: [{ children: allDocumentChildren }] }));
}

export function generateCoachingReportCSV(data: ParticipantData[]): Blob {
  if (data.length === 0) {
    throw new Error('No participant data available');
  }

  // Collect all unique questions across all participants to build column headers
  const allQuestions = new Set<string>();
  for (const pt of data) {
    for (const call of pt.coachingCalls) {
      if (call.assessmentQuestions) {
        for (const qa of call.assessmentQuestions) {
          allQuestions.add(qa.questionText);
        }
      }
    }
  }

  const questionsList = [...allQuestions].sort();

  // Create headers with actual question text
  const headers = [
    'symon_ppt_id',
    'interview_date_bl',
    'interviewer_bl',
    'interview_week',
    ...questionsList,
  ];

  const csvRows: string[] = [];
  csvRows.push(headers.join('\t'));

  for (const pt of data) {
    const studyCode = pt.studyName?.replace(/\s+/g, '') || 'CC012CD';
    const paddedId = pt.participantId.toString().padStart(5, '0');
    const participantCode = `${studyCode}-${paddedId}`;

    if (pt.coachingCalls.length === 0) {
      // Add row with participant info but no coaching data
      const row = [participantCode, '', '', '0', ...questionsList.map(() => '')];
      csvRows.push(row.join('\t'));
      continue;
    }

    // For each coaching call, create a row
    for (const [callIndex, call] of pt.coachingCalls.entries()) {
      const callDate = new Date(call.callDate).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });

      // Create answers map
      const answersMap = new Map<string, string>();
      if (call.assessmentQuestions) {
        for (const qa of call.assessmentQuestions) {
          answersMap.set(qa.questionText, qa.answerValue || '');
        }
      }

      // Build row data
      const row = [
        participantCode,
        callDate,
        call.coachName || 'Ross',
        callIndex.toString(),
        ...questionsList.map((question) => answersMap.get(question) || ''),
      ];

      csvRows.push(row.join('\t'));
    }
  }

  return new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
}

export function coachingReportFilename(data: ParticipantData[], format: 'docx' | 'csv'): string {
  if (data.length === 0) {
    return `coaching-report.${format}`;
  }

  const pt = data[0]!;
  const today = new Date()
    .toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    })
    .replace(/\//g, '.');

  const studyCode = pt.studyName?.replace(/\s+/g, '') || 'Study';
  const paddedId = pt.participantId.toString().padStart(5, '0');

  if (data.length > 1) {
    return `${studyCode}-Multiple-Participants Coaching Report ${today}.${format}`;
  }

  return `${studyCode}-${paddedId} Coaching Report ${today}.${format}`;
}
